/**
 * Phantom Wallet Warning Monitor
 * 
 * Detects and logs Phantom wallet warnings for debugging.
 * Helps identify issues that trigger "dApp could be malicious" warnings.
 */

interface PhantomWarning {
  type: 'transaction' | 'connection' | 'signature' | 'simulation' | 'unknown';
  message: string;
  timestamp: Date;
  context?: Record<string, unknown>;
}

const warningLog: PhantomWarning[] = [];

/**
 * Log a Phantom-related warning for debugging
 */
export function logPhantomWarning(
  type: PhantomWarning['type'],
  message: string,
  context?: Record<string, unknown>
): void {
  const warning: PhantomWarning = {
    type,
    message,
    timestamp: new Date(),
    context,
  };
  
  warningLog.push(warning);
  
  // Keep only last 100 warnings
  if (warningLog.length > 100) {
    warningLog.shift();
  }
  
  console.warn(`[Phantom Warning] ${type}: ${message}`, context);
}

/**
 * Get all logged Phantom warnings
 */
export function getPhantomWarnings(): PhantomWarning[] {
  return [...warningLog];
}

/**
 * Clear all logged warnings
 */
export function clearPhantomWarnings(): void {
  warningLog.length = 0;
}

/**
 * Check if Phantom wallet is available
 */
export function isPhantomAvailable(): boolean {
  return typeof window !== 'undefined' && 
         'solana' in window && 
         (window as any).solana?.isPhantom === true;
}

/**
 * Monitor Phantom connection events
 */
export function setupPhantomMonitor(): () => void {
  if (typeof window === 'undefined') return () => {};
  
  const phantom = (window as any).solana;
  if (!phantom?.isPhantom) return () => {};
  
  const handleConnect = (publicKey: any) => {
    console.log('[Phantom Monitor] Connected:', publicKey?.toBase58?.());
  };
  
  const handleDisconnect = () => {
    console.log('[Phantom Monitor] Disconnected');
  };
  
  const handleAccountChange = (publicKey: any) => {
    if (publicKey) {
      console.log('[Phantom Monitor] Account changed:', publicKey.toBase58());
    } else {
      console.log('[Phantom Monitor] Account disconnected');
    }
  };
  
  // Listen to Phantom events
  phantom.on('connect', handleConnect);
  phantom.on('disconnect', handleDisconnect);
  phantom.on('accountChanged', handleAccountChange);
  
  // Return cleanup function
  return () => {
    phantom.off('connect', handleConnect);
    phantom.off('disconnect', handleDisconnect);
    phantom.off('accountChanged', handleAccountChange);
  };
}

/**
 * Validate transaction before sending to prevent Phantom warnings
 */
export function validateTransactionForPhantom(transaction: any): { valid: boolean; issues: string[] } {
  const issues: string[] = [];
  
  // Check if transaction has recent blockhash
  if (!transaction.recentBlockhash) {
    issues.push('Missing recentBlockhash');
  }
  
  // Check if transaction has fee payer
  if (!transaction.feePayer) {
    issues.push('Missing feePayer');
  }
  
  // Check if transaction has instructions
  if (!transaction.instructions || transaction.instructions.length === 0) {
    issues.push('No instructions in transaction');
  }
  
  // Check for potential issues with instructions
  for (let i = 0; i < (transaction.instructions || []).length; i++) {
    const ix = transaction.instructions[i];
    
    // Check for missing program ID
    if (!ix.programId) {
      issues.push(`Instruction ${i}: Missing programId`);
    }
    
    // Check for empty keys array
    if (!ix.keys || ix.keys.length === 0) {
      issues.push(`Instruction ${i}: No account keys`);
    }
  }
  
  if (issues.length > 0) {
    logPhantomWarning('transaction', 'Transaction validation failed', { issues });
  }
  
  return {
    valid: issues.length === 0,
    issues,
  };
}

/**
 * Check domain trust indicators
 */
export function checkDomainTrust(): { score: number; issues: string[] } {
  const issues: string[] = [];
  let score = 100;
  
  // Check HTTPS
  if (typeof window !== 'undefined' && window.location.protocol !== 'https:') {
    issues.push('Site not using HTTPS');
    score -= 30;
  }
  
  // Check for favicon
  const favicon = document.querySelector('link[rel="icon"]');
  if (!favicon) {
    issues.push('Missing favicon');
    score -= 5;
  }
  
  // Check for meta tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (!ogTitle || !ogDescription) {
    issues.push('Missing Open Graph meta tags');
    score -= 5;
  }
  
  // Check for structured data
  const structuredData = document.querySelector('script[type="application/ld+json"]');
  if (!structuredData) {
    issues.push('Missing structured data');
    score -= 5;
  }
  
  return { score: Math.max(0, score), issues };
}

/**
 * Export diagnostic report for debugging
 */
export function exportDiagnosticReport(): string {
  const report = {
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : 'unknown',
    phantom: {
      available: isPhantomAvailable(),
      version: typeof window !== 'undefined' ? (window as any).solana?.version : 'unknown',
    },
    domainTrust: checkDomainTrust(),
    warnings: getPhantomWarnings(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  };
  
  return JSON.stringify(report, null, 2);
}
